import {
  messageResponseSchema,
  messageThreadDetailResponseSchema,
  messageThreadListResponseSchema,
  messageThreadResponseSchema,
  type ListMessageThreadsQuery,
  type Message,
  type MessageThread,
  type MessageThreadDetailResponse,
  type SendMessageRequest,
} from '@emr/shared';

import { ApiError, apiRequest } from './client.ts';

/**
 * The staff inbox surface (`BACKLOG.md` W2, pillar 3, `docs/decisions/patient-portal-messaging-
 * scope.md`) — `server/src/messaging/routes.ts`'s `/api/inbox/*`, validated against `@emr/shared`'s
 * own schemas, matching `api/appointments.ts`'s own stated convention rather than a locally-invented
 * approximation of the response shape.
 *
 * This is the staff half only. The patient-portal half (`server/src/patientMessaging/routes.ts`) has
 * no web client anywhere in this app yet — there is no patient-portal web frontend at all
 * (`BACKLOG.md` W4) — so there is nothing here to call it.
 */

export type { Message, MessageThread, MessageThreadDetailResponse };

/** `GET /api/inbox/threads` — every `care_team`/`general` thread this caller may see, already
 * confidentiality-filtered by the server. `query.kind` omitted returns both kinds in one call, same
 * as the server's own "kind undefined means union of both" branch. */
export async function listThreads(
  query: ListMessageThreadsQuery,
  signal?: AbortSignal,
): Promise<readonly MessageThread[]> {
  const params = new URLSearchParams();
  if (query.kind !== undefined) params.set('kind', query.kind);
  if (query.status !== undefined) params.set('status', query.status);
  if (query.assigned !== undefined) params.set('assigned', query.assigned);
  const qs = params.toString();
  const response = await apiRequest(
    qs === '' ? '/api/inbox/threads' : `/api/inbox/threads?${qs}`,
    messageThreadListResponseSchema,
    signal === undefined ? {} : { signal },
  );
  return response.threads;
}

/** `GET /api/inbox/threads/:threadId` — thread plus every message, oldest first. Marks the thread
 * read for this caller as a server-side side effect (`markThreadRead`, `messaging/routes.ts`); this
 * client does nothing extra to make that happen, and nothing here reads that state back (see
 * `inbox/useUnassignedInboxCount.ts`'s own header for why the nav badge counts unassigned threads,
 * not unread ones). */
export async function getThread(
  threadId: string,
  signal?: AbortSignal,
): Promise<MessageThreadDetailResponse> {
  return apiRequest(
    `/api/inbox/threads/${threadId}`,
    messageThreadDetailResponseSchema,
    signal === undefined ? {} : { signal },
  );
}

/** `POST /api/inbox/threads/:threadId/messages` — a staff reply. Triggers a patient email
 * notification server-side; nothing for this client to do beyond the request itself. */
export async function replyToThread(threadId: string, input: SendMessageRequest): Promise<Message> {
  const response = await apiRequest(
    `/api/inbox/threads/${threadId}/messages`,
    messageResponseSchema,
    { method: 'POST', body: input },
  );
  return response.message;
}

/** `POST /api/inbox/threads/:threadId/claim` — optimistic claim, `general` threads only. Throws
 * `ApiError` with `code === 'thread_already_claimed'` (409, lost the race) or
 * `code === 'thread_not_claimable'` (409, a `care_team` thread — see `inbox/threadRules.ts`'s
 * `isClaimable`, which is what keeps the UI from ever offering this call on one). */
export async function claimThread(threadId: string): Promise<MessageThread> {
  const response = await apiRequest(
    `/api/inbox/threads/${threadId}/claim`,
    messageThreadResponseSchema,
    { method: 'POST', body: {} },
  );
  return response.thread;
}

/** `POST /api/inbox/threads/:threadId/close` — either kind (`messaging/service.ts`'s `closeThread`
 * comment: "Close a thread — either kind"). */
export async function closeThread(threadId: string): Promise<MessageThread> {
  const response = await apiRequest(
    `/api/inbox/threads/${threadId}/close`,
    messageThreadResponseSchema,
    { method: 'POST', body: {} },
  );
  return response.thread;
}

/**
 * The messaging-specific error vocabulary (`MESSAGING_ERROR_CODES`, `@emr/shared`), turned into a
 * message staff can act on — matching `api/appointments.ts`'s `appointmentErrorMessage` in both
 * shape and register (plain English, not run through i18n — this file follows that existing
 * precedent rather than introducing a different convention for the first domain that needs one).
 */
export function messagingErrorMessage(error: unknown): string {
  if (!(error instanceof ApiError)) {
    return error instanceof Error ? error.message : 'Something went wrong.';
  }
  switch (error.code) {
    case 'thread_access_denied':
      return error.denialReason === 'noCareTeamRelationship'
        ? 'You no longer have an active care-team relationship with this patient.'
        : 'This thread is restricted, and you do not have a permitted reason to view it.';
    case 'thread_already_claimed':
      return 'Someone else already claimed this thread.';
    case 'thread_not_claimable':
      return 'A care-team thread has no single owner and cannot be claimed.';
    case 'not_found':
      return 'That thread no longer exists.';
    case 'audit_unavailable':
      return 'The change could not be recorded and was not made. Try again in a moment.';
    default:
      return error.message;
  }
}
